// ============================================================================
// Stale Chunk Guard
// ============================================================================
// Vite names every lazily-imported chunk with a content hash, so each deploy
// invalidates the previous build's chunk URLs. A session loaded before a
// deploy (common in the desktop app, which stays open for days) then fails
// every not-yet-loaded dynamic import with "Failed to fetch dynamically
// imported module". This guard catches that failure globally - via Vite's
// vite:preloadError event (fired by the built preload helper even when the
// caller catches the import error) plus an unhandledrejection fallback - and
// reloads the page once so the session picks up the new build.
//
// Guard rails around the reload:
//   - Only module-LOAD failures qualify (message match). A chunk that loads
//     but throws during evaluation is an app bug; reloading would loop.
//   - The host is HEAD-probed first: navigator.onLine only says the network
//     interface is up, and reloading during a server outage would replace a
//     still-working shell with the WebView's dead error page.
//   - A per-tab sessionStorage cooldown stops a reload loop when chunks keep
//     failing right after a recovery reload (outage, not a stale build).
//   - Not armed on the dev server: import failures there are transform errors
//     and the reload would wipe Vite's error overlay.
// ============================================================================

import { showToast } from 'shared/toast/toast.js';

// sessionStorage (per-tab) so one window's recovery reload never suppresses
// another window's, and the stamp dies with the tab.
const RELOAD_STAMP_KEY = 'staleChunkReloadAt';
const RELOAD_COOLDOWN_MS = 60000;
// Long enough for the toast to be read and for any in-flight save the failed
// import was part of (e.g. a lazy step inside a task-create flow) to land.
const RELOAD_DELAY_MS = 3500;
const PROBE_TIMEOUT_MS = 5000;
// A stamp younger than this at boot means this page load IS a recovery reload.
const ANNOUNCE_WINDOW_MS = 10000;

const OUTAGE_MESSAGE = 'Part of the app failed to load. Check your connection, then reload.';

// Failed module-import wording differs per engine (Chrome / Firefox / Safari);
// "Unable to preload CSS" is Vite's own wording for a failed CSS dependency.
const MODULE_LOAD_ERROR_PATTERNS = [
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
    'Unable to preload CSS'
];

/**
 * Whether an error is a failed module LOAD (chunk unreachable) rather than a
 * module that loaded and then threw. Callers that render their own error UI
 * (e.g. the router) use this to show "updating" instead of a raw stack.
 * @param {any} reason
 * @returns {boolean}
 */
export function isModuleLoadError(reason) {
    const message = reason?.message || '';
    return MODULE_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

class StaleChunkGuard {
    constructor() {
        this.reloadPending = false;
        // The vite:preloadError path rethrows to the caller, so the same error
        // usually arrives a second time as an unhandled rejection. WeakSet so
        // handled errors stay GC-able.
        this.handledErrors = new WeakSet();
        this.onPreloadError = (event) => {
            // Only module-load failures. Anything else (e.g. a module-scope
            // throw inside the imported chunk) rethrows to the caller
            // untouched - the guard never preventDefaults the vite event.
            if (!isModuleLoadError(event.payload)) return;
            this.handleFailure(event.payload);
        };
        this.onUnhandledRejection = (event) => {
            if (!isModuleLoadError(event.reason)) return;
            // The failure is handled here (or already was, via the vite
            // event) - keep the raw rejection out of the console.
            event.preventDefault();
            this.handleFailure(event.reason);
        };
    }

    // App-lifetime by design: armed once at boot, before the router so no lazy
    // route/module import can fire unguarded. Never torn down - the guard
    // outlives every page, so its listeners are intentionally permanent.
    init() {
        // Statically replaced at build time, so this is dead code in production.
        if (import.meta.env.DEV) return;

        window.addEventListener('vite:preloadError', this.onPreloadError);
        window.addEventListener('unhandledrejection', this.onUnhandledRejection);
        this.announceRecentReload();
    }

    async handleFailure(error) {
        if (this.reloadPending) return;
        if (error && typeof error === 'object') {
            if (this.handledErrors.has(error)) return;
            this.handledErrors.add(error);
        }

        console.warn('[StaleChunkGuard] Dynamic import failed:', error?.message || error);

        // Offline: reloading now would drop the whole app shell, and the
        // connection-status banner already explains the problem.
        if (navigator.onLine === false) {
            showToast('You are offline - that action could not load. Retry once reconnected.', 'error');
            return;
        }

        // Reloaded moments ago and imports still fail: the server is not
        // serving chunks (outage), not a stale build. Do not loop.
        const lastReloadAt = Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
        if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
            showToast(OUTAGE_MESSAGE, 'error', 8000);
            return;
        }

        // reloadPending also parks any further failures while the probe runs.
        this.reloadPending = true;
        const reachable = await this.isServerReachable();
        if (!reachable) {
            this.reloadPending = false;
            showToast(OUTAGE_MESSAGE, 'error', 8000);
            return;
        }

        sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
        // The probe round-trip also ordered this toast AFTER any error toast
        // the failing caller showed from its own catch, so the simple-toast
        // dedup leaves THIS explanation on screen, not a generic failure.
        showToast('A new version of AERIUM is available - reloading...', 'info', RELOAD_DELAY_MS + 2000);
        setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    }

    // Can the host serve documents right now? Bounded so a black-holed probe
    // cannot leave reloadPending stuck forever.
    async isServerReachable() {
        try {
            const response = await fetch(window.location.pathname, {
                method: 'HEAD',
                cache: 'no-store',
                signal: AbortSignal.timeout(PROBE_TIMEOUT_MS)
            });
            return response.ok;
        } catch (_) {
            return false;
        }
    }

    // If this boot happened right after a guard reload, say so - the user saw
    // the app restart itself and deserves the explanation even if they missed
    // the pre-reload toast (or a caller's error toast replaced it).
    announceRecentReload() {
        const lastReloadAt = Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
        if (lastReloadAt && Date.now() - lastReloadAt < ANNOUNCE_WINDOW_MS) {
            showToast('AERIUM refreshed itself to pick up the latest version', 'success', 5000);
        }
    }
}

export const staleChunkGuard = new StaleChunkGuard();
